import { useCallback, useEffect, useState } from 'react';
import { TariffCodeApi, TariffCategoryApi } from '@shared/sdk-v1';
import type { TariffCode, TariffCategory, CustomsEstimate } from '@shared/sdk-v1';
import { apiConfig } from '../shared/api-config';

interface UseCustomsEstimateReturn {
  categories: TariffCategory[];
  search: (q: string, categoryName?: string) => Promise<TariffCode[]>;
  /** Resolves null (and sets `error`) when the estimate is unavailable, so callers fall back. */
  estimate: (tariffCode: string, itemValueUsd: number) => Promise<CustomsEstimate | null>;
  breakdown: CustomsEstimate | null;
  clear: () => void;
  isLoading: boolean;
  error: Error | null;
}

/** Tariff-code customs estimate (CourierSettings.isCustomsCalculatorEnabled). */
export const useCustomsEstimate = (courierId: number | undefined, enabled: boolean): UseCustomsEstimateReturn => {
  const [categories, setCategories] = useState<TariffCategory[]>([]);
  const [breakdown, setBreakdown] = useState<CustomsEstimate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    new TariffCategoryApi(apiConfig())
      .tariffCategoryFind({ filter: JSON.stringify({ where: { isActive: true }, order: 'displayOrder ASC' }) })
      .then((rows) => {
        if (!cancelled) setCategories(rows);
      })
      .catch(() => {
        // Chips are optional; search still works without them.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const search = useCallback(
    (q: string, categoryName?: string) =>
      new TariffCodeApi(apiConfig()).tariffCodeSearch({ q, categoryName, limit: 20 }),
    []
  );

  const estimate = useCallback(
    async (tariffCode: string, itemValueUsd: number) => {
      if (courierId == null) return null;
      setIsLoading(true);
      setError(null);
      try {
        const result = await new TariffCodeApi(apiConfig()).tariffCodeCalculateCustoms({
          courierId,
          tariffCode,
          itemValueUsd,
        });
        setBreakdown(result);
        return result;
      } catch (err) {
        setBreakdown(null);
        setError(err as Error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [courierId]
  );

  const clear = useCallback(() => setBreakdown(null), []);

  return { categories, search, estimate, breakdown, clear, isLoading, error };
};
